import React, { useEffect, useState } from "react";
import {
  List,
  Card,
  Image,
  Spin,
  Typography,
  InputNumber,
  Button,
  message,
  Popconfirm,
  Modal,
  Input,
  Empty,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

const Cart = ({ refreshCartCount }) => {
  const navigate = useNavigate();
  const [cartData, setCartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [detailModal, setDetailModal] = useState({
    open: false,
    item: null,
    value: "",
  });

  const isMobile = useMediaQuery({ maxWidth: 767 });

  useEffect(() => {
    checkUserActiveStatus();
    fetchCartData();
    // eslint-disable-next-line
  }, []);

  const checkUserActiveStatus = async () => {
    const tokenString = localStorage.getItem("token");

    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL + "api/UserAccount/GetUserAccountDetail",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      axios
        .request(config)
        .then((response) => {
          // Redirect to home if AllowtoOrder is false
          if (response.data.AllowtoOrder === false) {
            window.location.href = "/";
            return;
          }

          if (
            response.data.IsActive === false ||
            response.data.IsDeleted === false
          ) {
            alert("Your session has expired. Please log in again.");

            localStorage.removeItem("token");
            localStorage.removeItem("userName");
            window.location.reload();
          }
        })
        .catch((error) => {});
    } catch (error) {}
  };

  const fetchCartData = async () => {
    setLoading(true);
    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.get(
        global.config.APIURL.BaseURL + "api/UserCart/GetCartData",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      setCartData(response.data || []);
    } catch (error) {
      setCartData([]);
    }
    setLoading(false);
  };

  const handleQtyChange = async (item, newNoOfBox) => {
    if (newNoOfBox < 1) return;
    setUpdatingId(item.ID);

    // Calculate new dependent values
    const NoOfBox = newNoOfBox;
    const BoxSize = item.BoxSize;
    const TotalQuantity = NoOfBox * BoxSize;
    const Price = item.Price;
    const Amount = Price * TotalQuantity;

    // Prepare payload (all other data remains same)
    const payload = {
      ID: item.ID,
      UserID: item.UserID,
      ProductID: item.ProductID,
      Color: item.Color,
      Size: item.Size,
      NoOfBox,
      BoxSize,
      TotalQuantity,
      Price,
      Amount,
      Detail: item.Detail,
      AddedOn: item.AddedOn,
      LastUpdatedOn: new Date(),
    };

    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.post(
        global.config.APIURL.BaseURL + "api/UserCart/UpdateQtyInCart",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      if (response.status === 200) {
        message.success("Quantity updated!");
        setCartData((prev) =>
          prev.map((cartItem) =>
            cartItem.ID === item.ID
              ? {
                  ...cartItem,
                  NoOfBox,
                  TotalQuantity,
                  Amount,
                }
              : cartItem
          )
        );
      } else {
        message.error("Failed to update quantity.");
      }
    } catch (error) {
      message.error("Failed to update quantity.");
    }
    setUpdatingId(null);
  };

  const handleRemove = async (cartId) => {
    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.delete(
        global.config.APIURL.BaseURL +
          `api/UserCart/DeleteFromCart?CartID=${cartId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      if (response.status === 200) {
        message.success("Removed from cart!");
        setCartData((prev) => prev.filter((item) => item.ID !== cartId));
        if (refreshCartCount) refreshCartCount();
      } else {
        message.error("Failed to remove from cart.");
      }
    } catch (error) {
      message.error("Failed to remove from cart.");
    }
  };

  const openDetailModal = (item) => {
    setDetailModal({ open: true, item, value: item.Detail || "" });
  };

  const handleDetailUpdate = async () => {
    const { item, value } = detailModal;
    if (!item) return;
    const payload = {
      ...item,
      Detail: value,
      LastUpdatedOn: new Date(),
    };
    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.post(
        global.config.APIURL.BaseURL + "api/UserCart/UpdateSpecificationInCart",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      if (response.status === 200) {
        message.success("Detail updated!");
        setCartData((prev) =>
          prev.map((cartItem) =>
            cartItem.ID === item.ID ? { ...cartItem, Detail: value } : cartItem
          )
        );
        setDetailModal({ open: false, item: null, value: "" });
      } else {
        message.error("Failed to update detail.");
      }
    } catch (error) {
      message.error("Failed to update detail.");
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  const totalBox = cartData.reduce((sum, item) => sum + (item.NoOfBox || 0), 0);
  const totalAmount = cartData.reduce(
    (sum, item) => sum + (item.Amount || 0),
    0
  );
  // Total Items by unique DesignNo
  const totalItems = new Set(cartData.map((item) => item.DesignNo)).size;

  return (
    <div style={{ padding: 0, margin: 0 }}>
      <Card style={{ padding: 0, margin: 0 }}>
        <div
          style={{
            margin: isMobile ? "0" : "auto",
            padding: isMobile ? 8 : 24,
          }}
        >
          <Title
            level={2}
            style={{
              fontSize: isMobile ? 22 : 28,
              textAlign: isMobile ? "center" : "left",
            }}
          >
            Your Cart
          </Title>
          <List
            grid={{ gutter: isMobile ? 8 : 16, column: 1 }}
            dataSource={cartData}
            locale={{
              emptyText: (
                <Empty
                  description="No products in cart."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
            renderItem={(item) => (
              <List.Item style={{ padding: isMobile ? 4 : 5 }}>
                <Card
                  style={{
                    border: "1px solid #3492eb",
                    padding: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: isMobile ? 8 : 0,
                    }}
                  >
                    {/* First row: Image + ProductName, Design No, Color, Size (mobile) */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: isMobile ? "flex-start" : "center",
                        gap: isMobile ? 8 : 24,
                        width: "100%",
                      }}
                    >
                      <Image
                        src={item.ImageUrl}
                        alt={item.ProductName}
                        width={isMobile ? 60 : 100}
                        height={isMobile ? 60 : 100}
                        style={{
                          objectFit: "cover",
                          borderRadius: 8,
                          margin: 0,
                          minWidth: isMobile ? 60 : 100,
                        }}
                        fallback="https://via.placeholder.com/100"
                      />
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          gap: 2,
                        }}
                      >
                        <Title
                          level={4}
                          style={{
                            margin: 0,
                            fontSize: isMobile ? 15 : 20,
                            fontWeight: 700,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.ProductName}
                        </Title>
                        <Text
                          type="secondary"
                          style={{
                            fontSize: isMobile ? 12 : 14,
                            lineHeight: 1.2,
                            fontWeight: 400,
                            display: "block",
                            marginTop: 2,
                          }}
                        >
                          Design No: {item.DesignNo}
                        </Text>
                        <Text
                          style={{
                            fontSize: isMobile ? 12 : 14,
                            lineHeight: 1.2,
                            display: "block",
                            fontWeight: 400,
                            marginTop: 2,
                          }}
                        >
                          Color:{" "}
                          {item.Color && item.Color.trim()
                            ? item.Color
                            : "Mix Color"}{" "}
                          | Size: {item.Size}
                        </Text>
                      </div>
                    </div>
                    {/* Second row: Other details in a single column for mobile, two columns for desktop */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        gap: 8,
                        marginTop: isMobile ? 8 : 0,
                      }}
                    >
                      {/* Left column (or top block in mobile) */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          style={{
                            fontSize: isMobile ? 12 : 14,
                            display: "block",
                          }}
                        >
                          <b>Box Size:</b> {item.BoxSize}
                        </Text>
                        <Text
                          style={{
                            fontSize: isMobile ? 12 : 14,
                            display: "block",
                          }}
                        >
                          <b>Total Qty:</b> {item.TotalQuantity}
                        </Text>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            margin: "6px 0",
                            flexWrap: "wrap",
                          }}
                        >
                          <Text>Box:</Text>
                          <Button
                            icon={<MinusOutlined />}
                            size="small"
                            onClick={() =>
                              handleQtyChange(item, item.NoOfBox - 1)
                            }
                            disabled={
                              item.NoOfBox <= 1 || updatingId === item.ID
                            }
                          />
                          <InputNumber
                            min={1}
                            value={item.NoOfBox}
                            onChange={(value) => handleQtyChange(item, value)}
                            style={{
                              width: isMobile ? 36 : 60,
                              textAlign: "center",
                              fontSize: isMobile ? 13 : 15,
                            }}
                            disabled={updatingId === item.ID}
                          />
                          <Button
                            icon={<PlusOutlined />}
                            size="small"
                            onClick={() =>
                              handleQtyChange(item, item.NoOfBox + 1)
                            }
                            disabled={updatingId === item.ID}
                          />
                          <span style={{ fontSize: isMobile ? 12 : 14 }}>
                            × {item.BoxSize} = {item.TotalQuantity}
                          </span>
                        </div>
                      </div>
                      {/* Right column (or bottom block in mobile) */}
                      <div
                        style={{
                          minWidth: isMobile ? 0 : 220,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: isMobile ? "flex-start" : "flex-start",
                          justifyContent: "flex-start",
                          width: isMobile ? "100%" : "auto",
                          marginTop: isMobile ? 8 : 0,
                        }}
                      >
                        <Text style={{ fontSize: isMobile ? 13 : 15 }}>
                          Price: ₹{item.Price.toFixed(2)}
                        </Text>
                        <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
                          Total: ₹{item.Amount.toFixed(2)}
                        </Text>
                        {item.Detail && (
                          <Text
                            type="secondary"
                            style={{
                              fontSize: isMobile ? 13 : 15,
                              display: "block",
                              marginTop: 2,
                              color: "#3492eb",
                            }}
                          >
                            Detail: {item.Detail}
                          </Text>
                        )}
                        <div style={{ marginTop: 4 }}>
                          <Button
                            icon={<EditOutlined />}
                            size="small"
                            onClick={() => openDetailModal(item)}
                            style={{
                              marginRight: 8,
                              marginBottom: isMobile ? 4 : 0,
                            }}
                          >
                            Update Detail
                          </Button>
                          <Popconfirm
                            title="Are you sure to remove this item from cart?"
                            onConfirm={() => handleRemove(item.ID)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              icon={<DeleteOutlined />}
                              size="small"
                              danger
                              loading={updatingId === item.ID}
                            >
                              Remove
                            </Button>
                          </Popconfirm>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </List.Item>
            )}
          />
          {/* Totals at the bottom */}

          <div
            style={{
              marginTop: isMobile ? 16 : 32,
              padding: isMobile ? 8 : 16,
              background: "#fafafa",
              borderRadius: 8,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              justifyContent: isMobile ? "flex-start" : "flex-end",
              gap: isMobile ? 8 : 40,
              fontSize: isMobile ? 15 : 18,
              fontWeight: 500,
              position: isMobile ? "sticky" : "static",
              bottom: isMobile ? 0 : "unset",
              left: 0,
              right: 0,
              zIndex: 100,
            }}
          >
            <span>
              Total Items: <b>{totalItems}</b>
            </span>
            <span>
              Total Box: <b>{totalBox}</b>
            </span>
            <span>
              Total Amount: <b>₹{totalAmount.toFixed(2)}</b>
            </span>
            <Button
              type="primary"
              size={isMobile ? "middle" : "large"}
              style={{
                marginTop: isMobile ? 8 : 0,
                marginLeft: isMobile ? 0 : 24,
                width: isMobile ? "100%" : "auto",
                alignSelf: isMobile ? "stretch" : "center",
              }}
              onClick={() => {
                navigate("/cartoverview");
              }}
              disabled={cartData.length === 0} // <-- Disable if cart is empty
            >
              Place Order
            </Button>
          </div>
          <Modal
            title="Update Detail"
            open={detailModal.open}
            onOk={handleDetailUpdate}
            onCancel={() =>
              setDetailModal({ open: false, item: null, value: "" })
            }
            okText="Update"
            cancelText="Cancel"
          >
            <Input.TextArea
              rows={3}
              value={detailModal.value}
              onChange={(e) =>
                setDetailModal((prev) => ({ ...prev, value: e.target.value }))
              }
              placeholder="Enter new detail"
            />
          </Modal>
        </div>
      </Card>
    </div>
  );
};

export default Cart;
